import React from 'react';
import { RouterProvider } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { ConfirmProvider } from 'material-ui-confirm';
import { ConfigContext, GlobalContextProvider } from './context/ConfigContext';
import { SnackbarUtilsConfigurator } from './components/SnackbarUtilsConfigurator';
import { FormDialogServiceProvider } from '@terragotech/gen5-shared-components';
import { ThemeProvider } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import blue from '@material-ui/core/colors/blue';
import {
  Apollo as ApolloProvider,
  AuthContext,
  ErrorBoundary,
  useAuthState,
  ServerPublicMetaContextProvider,
  SpinnerContextProvider,
  UserInfoProvider,
} from '@terragotech/gen5-shared-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { makeStyles } from '@material-ui/core/styles';
import { createAppTheme } from 'utils/themedTheme';
import { CssBaseline } from '@material-ui/core';
import { Router } from 'Router';
import { RedirectServiceProvider } from 'context/RedirectModalContext';
import { ConfirmServiceProvider } from 'context/ConfirmContext';
import { HistoryContextProvider } from 'context/HistoryContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    position: 'fixed',
    inset: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
});

// Main App component wrapped with theme mode provider
export const App: React.FC = () => {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
};

const AppContent: React.FC = () => {
  const classes = useStyles();
  const authState = useAuthState();
  const { token, clearToken } = authState;
  const { mode } = useThemeMode();

  React.useEffect(() => {
    document.body.setAttribute('data-theme', mode);
    return () => {
      document.body.removeAttribute('data-theme');
    };
  }, [mode]);

  return (
    <ErrorBoundary>
      <ApolloProvider
        token={token}
        onTokenExpired={clearToken}
        uri={
          process.env.NODE_ENV === 'production'
            ? '/graphql'
            : `${process.env.REACT_APP_SERVER_URL || 'http://localhost:3002'}/graphql`
        }
        onRedirectLogin={() => {
          console.log('Needs login to perform this action');
        }}
      >
        <AuthContext.Provider value={authState}>
          <UserInfoProvider>
            <GlobalContextProvider>
              <ConfigContext.Consumer>
                {(configContext) => {
                  return (
                    <ThemeProvider
                      theme={createAppTheme(
                        configContext?.config?.webUIConfig?.theme?.primary || blue[600],
                        configContext?.config?.webUIConfig?.theme?.secondary || blue[600],
                        mode
                      )}
                    >
                      <StyledThemeProvider
                        theme={createAppTheme(
                          configContext?.config?.webUIConfig?.theme?.primary || blue[600],
                          configContext?.config?.webUIConfig?.theme?.secondary || blue[600],
                          mode
                        )}
                      >
                        <CssBaseline />
                        <ServerPublicMetaContextProvider
                          redirectSignInField="redirectConfigEditorSignIn"
                          redirectSignOutField="redirectConfigEditorSignOut"
                          renderLoading={() => (
                            <div className={classes.container}>
                              <CircularProgress color="primary" />
                            </div>
                          )}
                        >
                          <SpinnerContextProvider>
                            {/* @ts-ignore */}
                            <ConfirmProvider>
                              <SnackbarProvider
                                maxSnack={3}
                                anchorOrigin={{
                                  vertical: 'top',
                                  horizontal: 'right',
                                }}
                              >
                                <SnackbarUtilsConfigurator />
                                <DndProvider backend={HTML5Backend}>
                                  <HistoryContextProvider>
                                    <FormDialogServiceProvider>
                                      <RedirectServiceProvider>
                                        <ConfirmServiceProvider>
                                          <RouterProvider router={Router} />
                                        </ConfirmServiceProvider>
                                      </RedirectServiceProvider>
                                    </FormDialogServiceProvider>
                                  </HistoryContextProvider>
                                </DndProvider>
                              </SnackbarProvider>
                            </ConfirmProvider>
                          </SpinnerContextProvider>
                        </ServerPublicMetaContextProvider>
                      </StyledThemeProvider>
                    </ThemeProvider>
                  );
                }}
              </ConfigContext.Consumer>
            </GlobalContextProvider>
          </UserInfoProvider>
        </AuthContext.Provider>
      </ApolloProvider>
    </ErrorBoundary>
  );
};
